import { CommonModule } from '@angular/common';
import { Component, Injector, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';
import { ContactUsMessages } from 'src/app/core/data/model/contact-us-messages-model';
import { BlocState, InitEvent, ReadyState } from 'src/app/shared/bloc/base-bloc';
import { ColorsConstants, ContactUsMessageTableAction } from 'src/app/shared/common';
import { BasePaginatorComponent } from 'src/app/shared/component/base-paginator/base-paginator-component';
import { TableComponent } from 'src/app/shared/component/table/table.component';
import {
  BaseTableFiltersPage,
  ContactUsMessagesFilter,
  PaginationFilter,
  RowActionWithData,
  TableButtonSettings,
  TableColumn,
  TableConfigurations,
  TableFilter,
} from 'src/app/shared/interfaces';
import { DateFormatterPipe } from 'src/app/shared/pipes/date-formatter.pipe';
import { TablePaginationLayoutComponent } from 'src/app/shared/component/table-pagination-layout/table-pagination-layout.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PopupComponent } from 'src/app/shared/component/popup/popup.component';
import { ContactUsMessageDetails } from 'src/app/core/data/model/contact-us-message-details-model';
import { ContactUsMessagesFiltersComponent } from '../../components/contact-us-messages-filters/contact-us-messages-filters.component';
import { ContactUsPopupComponent } from '../../components/contact-us-popup/contact-us-popup.component';
import { ContactUsMessagesBloc } from '../../bloc/contact-us-messages-bloc';
import { UserType } from 'src/app/core/common';

@Component({
  selector: 'app-contact-us-messages',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    TranslateModule,
    ContactUsMessagesFiltersComponent,
    ReactiveFormsModule,
    DateFormatterPipe,
    TablePaginationLayoutComponent,
    MatDialogModule,
    ContactUsPopupComponent,
  ],
  templateUrl: './contact-us-messages.component.html',
  styleUrls: ['./contact-us-messages.component.scss'],
  providers: [ContactUsMessagesBloc],
})
export class ContactUsMessagesComponent
  extends BasePaginatorComponent
  implements
    OnInit,
    OnDestroy,
    BaseTableFiltersPage<ContactUsMessagesFilter, ContactUsMessageTableAction, ContactUsMessages>
{
  form!: FormGroup;
  tableConfigs: TableConfigurations = {
    isSelectable: false,
    showFilters: true,
  };
  tableColumns: TableColumn[];
  tableButtons: TableButtonSettings<ContactUsMessageTableAction, ContactUsMessages>[];
  tableData: ContactUsMessages[] = [];
  filterParams: ContactUsMessagesFilter;
  count: number = 0;

  UserType = UserType;

  // view child
  @ViewChild('popup', { static: true }) popUpComponent: TemplateRef<any>;
  constructor(injector: Injector, private bloc: ContactUsMessagesBloc, private dialog: MatDialog) {
    super(['DASHBOARD', 'BUTTONS'], injector);
    this.bloc.onState.pipe(takeUntil(this.destroy$)).subscribe((state) => this.onHandleBlocState(state));
  }

  ngOnInit(): void {
    this.getPaginatorConfigsFromURL();
    this.initializeTableColumns();
    this.initializeTableButtons();
  }

  initializeTableColumns(): void {
    this.tableColumns = [
      {
        name: this.translationsList['DASHBOARD']['MESSAGES']['TABLE']['ID'],
        dataKey: 'user.userId',
        hideDefaultCell: true,
        showCustomTemplateCell: true,
      },
      {
        name: this.translationsList['DASHBOARD']['MESSAGES']['TABLE']['NAME'],
        dataKey: 'user.fullName',
      },
      {
        name: this.translationsList['DASHBOARD']['MESSAGES']['TABLE']['EMAIL'],
        dataKey: 'user.email',
      },
      {
        name: this.translationsList['DASHBOARD']['MESSAGES']['TABLE']['PHONE_NUMBER'],
        dataKey: 'user.phone',
      },
      {
        name: this.translationsList['DASHBOARD']['MESSAGES']['TABLE']['TYPE'],
        dataKey: 'userType',
        hideDefaultCell: true,
        showCustomTemplateCell: true,
      },
      {
        name: this.translationsList['DASHBOARD']['MESSAGES']['TABLE']['DATE'],
        dataKey: 'createdAt',
        hideDefaultCell: true,
        showCustomTemplateCell: true,
      },
      {
        name: this.translationsList['DASHBOARD']['MESSAGES']['TABLE']['MESSAGE_TITLE'],
        dataKey: 'message',
      },
    ];
  }

  initializeTableButtons(): void {
    this.tableButtons = [
      {
        title: this.translationsList['BUTTONS']['NO_REPLY_YET'],
        icon: 'visibility',
        actionToPerform: ContactUsMessageTableAction.SHOW_MESSAGE,
        backgroundColor: ColorsConstants.PRIMARY_COLOR,
        color: ColorsConstants.LIGHT_COLOR,
        transformButton: (button, element) => {
          if (element.isInformed) {
            button = {
              ...button,
              ...{
                backgroundColor: ColorsConstants.LIGHT_GREY_COLOR,
                color: ColorsConstants.GREY_COLOR,
                title: this.translationsList['BUTTONS']['SHOW_MESSAGES'],
              },
            };
          }
          return button;
        },
      },
    ];
  }

  getData(): void {
    this.bloc.setEvent.next(new InitEvent(this.filterParams));
  }

  getFilterData(filters: TableFilter<ContactUsMessagesFilter>): void {
    if (filters.isSearch) {
      this.resetPaginator();
    }
    this.filterParams = {
      ...filters.filter,
      ...{
        pageSize: this.pageSize,
        pageIndex: this.pageIndex,
        fromDate: filters.filter.dateRange?.start,
        toDate: filters.filter.dateRange?.end,
        dateRange: null,
      },
    };

    this.getData();
  }

  onActionClicked(action: RowActionWithData<ContactUsMessageTableAction, ContactUsMessages>) {
    switch (action.action) {
      case ContactUsMessageTableAction.SHOW_MESSAGE: {
        const dialogRef = this.dialog.open(PopupComponent<ContactUsMessageDetails[], ContactUsMessages>, {
          width: '800px',
          disableClose: true,
          data: {
            title: this.translationsList['DASHBOARD']['MESSAGES']['POPUP']['HEADER'],

            data: action.data,
          },
        });
        dialogRef.componentInstance.template = this.popUpComponent;
        dialogRef.componentInstance.initializeCaller = () =>
          this.bloc.dashboardRepository.getContactUsMessageDetails(action.data.id);
        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {
            if (res && res['isMessageHasReply']) {
              this.getData();
            }
          });
      }
    }
  }

  //protected
  protected override getPaginationFilter(paginationFilter: PaginationFilter): void {
    super.getPaginationFilter(paginationFilter);
    this.filterParams = { ...this.filterParams, ...paginationFilter };
    this.getData();
  }

  protected override onHandleBlocState(state: BlocState): void {
    super.onHandleBlocState(state);
    if (state instanceof ReadyState) {
      this.tableData = state.data;
      this.count = state.count;
    }
  }
}
